import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useInviteClient } from '../hooks/useInviteClient';

type InviteClientModalProps = {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  onSuccess?: () => void;
};

export function InviteClientModal({
  isOpen,
  onClose,
  providerId,
  onSuccess,
}: InviteClientModalProps) {
  const [formData, setFormData] = useState({
    clientFirstName: '',
    clientLastName: '',
    clientEmail: '',
    personalMessage: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutate, isPending, isError, error, isSuccess, reset } = useInviteClient(providerId, {
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        clientFirstName: '',
        clientLastName: '',
        clientEmail: '',
        personalMessage: '',
      });
      setErrors({});
      reset();
    }
  }, [isOpen, reset]);

  // Auto-close on success
  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        onClose();
      }, 500);
    }
  }, [isSuccess, onClose]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientFirstName.trim()) {
      newErrors.clientFirstName = 'First name is required';
    }

    if (!formData.clientLastName.trim()) {
      newErrors.clientLastName = 'Last name is required';
    }

    if (!formData.clientEmail.trim()) {
      newErrors.clientEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      newErrors.clientEmail = 'Please enter a valid email address';
    }

    if (formData.personalMessage && formData.personalMessage.length > 500) {
      newErrors.personalMessage = 'Personal message must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    mutate({
      clientFirstName: formData.clientFirstName.trim(),
      clientLastName: formData.clientLastName.trim(),
      clientEmail: formData.clientEmail.trim(),
      personalMessage: formData.personalMessage.trim() || undefined,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  const handleRetry = () => {
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Invite Client</h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error Message */}
        {isError && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">
                  {error?.message || 'Unable to send invitation at this time. Please try again later.'}
                </p>
                <button
                  onClick={handleRetry}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name *
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.clientFirstName}
              onChange={(e) =>
                setFormData({ ...formData, clientFirstName: e.target.value })
              }
              className={`mt-1 block w-full rounded-md border ${
                errors.clientFirstName ? 'border-red-300' : 'border-gray-300'
              } px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500`}
              disabled={isPending}
            />
            {errors.clientFirstName && (
              <p className="mt-1 text-sm text-red-600">{errors.clientFirstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name *
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.clientLastName}
              onChange={(e) =>
                setFormData({ ...formData, clientLastName: e.target.value })
              }
              className={`mt-1 block w-full rounded-md border ${
                errors.clientLastName ? 'border-red-300' : 'border-gray-300'
              } px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500`}
              disabled={isPending}
            />
            {errors.clientLastName && (
              <p className="mt-1 text-sm text-red-600">{errors.clientLastName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address *
            </label>
            <input
              id="email"
              type="email"
              value={formData.clientEmail}
              onChange={(e) =>
                setFormData({ ...formData, clientEmail: e.target.value })
              }
              className={`mt-1 block w-full rounded-md border ${
                errors.clientEmail ? 'border-red-300' : 'border-gray-300'
              } px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500`}
              disabled={isPending}
            />
            {errors.clientEmail && (
              <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>
            )}
          </div>

          {/* Personal Message */}
          <div>
            <label htmlFor="personalMessage" className="block text-sm font-medium text-gray-700">
              Personal Message (Optional)
            </label>
            <textarea
              id="personalMessage"
              rows={4}
              value={formData.personalMessage}
              onChange={(e) =>
                setFormData({ ...formData, personalMessage: e.target.value })
              }
              placeholder="Add a personal note to your invitation..."
              className={`mt-1 block w-full rounded-md border ${
                errors.personalMessage ? 'border-red-300' : 'border-gray-300'
              } px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500`}
              disabled={isPending}
            />
            <div className="mt-1 flex items-center justify-between">
              {errors.personalMessage ? (
                <p className="text-sm text-red-600">{errors.personalMessage}</p>
              ) : (
                <p className="text-xs text-gray-500">
                  {formData.personalMessage.length}/500 characters
                </p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" data-testid="loading-spinner" />
                  Sending...
                </>
              ) : (
                'Send Invitation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
